/**
 * The patient panel of the clinic dashboard, drawn from Brian's 23 September
 * mockup (the Mary Beth screen): who the patient is, their goal in red, their
 * phenotype, then five tabs, the first being the three-step treatment builder.
 *
 * Every figure comes from the API: demographics and scores from
 * GET /patients/:id/workspace, the recommendation list and match labels from
 * the patient's own report (GET /patients/:id/recommendations), and the
 * Clinical Twin prediction from GET /regimens/preview for the exact
 * combination ticked. Nothing on it is typed in.
 *
 * Where the mockup and Brian's written brief disagree, the brief wins and the
 * difference is listed in the redesign artifact:
 *   - no "MCOS Overall Score" card: "Remove the Overall Score completely from
 *     both clinician and patient dashboards". That top-right slot holds the
 *     patient's status on the dashboard (Physician Alerts moved to the header
 *     on 26 September), and the three domain scores on the standalone page.
 *   - the message is not called AI-generated: there is no AI key yet, the
 *     message is assembled from the Twin figures (treatment-plan.service.ts).
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { Avatar, ErrorState, LoadingRows } from './ui';
import {
  IconArrowLeft,
  IconBars,
  IconBandage,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconFlask,
  IconInfo,
  IconLeaf,
  IconMolecule,
  IconNote,
  IconPencil,
  IconPill,
  IconScale,
  IconSend,
  IconSyringe,
  IconTrend,
  IconUsers,
} from './icons';
import { pricingBanner } from '@mcos/types';
import { TIER_STYLE, type RiskTier } from '../pages/Retain';
import { PlanStatus } from './PlanStatus';
import { ProtocolNotices } from './ProtocolNotices';
import { SignBanner, SignDrawer, type SignoffView } from './SignDrawer';
import { PriceBox, PricingBanner, bannerLines, useOprPrices } from './OprPricing';
import { OprPricingTab } from './OprPricingTab';
import { MicrodoseDoseCard } from './MicrodoseDoseCard';
import { BodyScanPanel } from './BodyScanPanel';
import { RecoveryCard, TwinMatchCard } from './MusclePathway';
import { HrtGoalBox, HrtStatusCard, HrtSummaryChips } from './HrtHeader';
import { HrtPlanTab, type HrtPick } from './HrtPlanTab';
import { HrtPathwayTab } from './HrtPathwayTab';
import { HrtLabsTab } from './HrtLabsTab';
import { HrtCheckinsTab } from './HrtCheckinsTab';
import { HrtTwinTab } from './HrtSections';
import { HrtOprPricing } from './HrtOprPricing';
import { useHrtProgram, useHrtView } from '../lib/hrt';

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

interface RegimenView {
  id: string;
  regimenCode: string;
  status: 'DRAFT' | 'ACTIVE' | 'STOPPED';
  selectedAt: string;
  startedAt: string | null;
  treatments: Array<{
    treatmentId: string;
    therapyId: string;
    name: string;
    category: string;
    dose: string | null;
    frequency: string | null;
    route: string | null;
  }>;
}

export interface WorkspaceData {
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    sexAtBirth: 'Male' | 'Female' | null;
    age: number | null;
    heightIn: number | null;
    weightLb: number | null;
    bmi: number | null;
    hasPhone: boolean;
    primaryGoal: string | null;
    goalHeadline: string | null;
    phenotypes: Array<{ tag: string; label: string }>;
  };
  scores: {
    reportId: string;
    generatedAt: string;
    metabolic: number | null;
    inflammation: number | null;
    liver: number | null;
  } | null;
  activeRegimen: RegimenView | null;
  draftRegimen: RegimenView | null;
  labs: {
    reportId: string;
    labReportId: string;
    collectionDate: string;
    domains: Array<{
      key: string;
      label: string;
      score: number | null;
      markers: Array<{
        markerId: string;
        name: string;
        value: number;
        unit: string;
        bandLabel: string;
        markerScore: number;
        flagged: boolean;
      }>;
    }>;
  } | null;
  history: Array<{ at: string; kind: string; title: string; detail: string | null }>;
  /** The dashboard's Patient status box, from `services/patient-status.ts`. */
  status: {
    programme: {
      month: number;
      cycleMonths: number;
      enrolledAt: string;
      subscriptionStatus: string;
      complete: boolean;
    } | null;
    nextDraw: {
      draw: 'BASELINE' | 'MONTH_3' | 'MONTH_6';
      label: string;
      dueAt: string | null;
      source: 'order' | 'plan';
    } | null;
    drawsComplete: boolean;
    retain: {
      month: number;
      assessedAt: string;
      score: number | null;
      tier: RiskTier;
      tierLabel: string;
    } | null;
    openLabAlerts: number;
  };
}

interface Recommendation {
  treatmentId: string;
  therapyId: string;
  name: string;
  category: string;
  class: string | null;
  route: string | null;
  useOrIndication: string | null;
  rank: number;
  recommended: boolean;
  priorityKey: 'highest' | 'high' | 'moderate' | 'low';
  matchLabel: 'High match' | 'Good match' | 'Moderate match' | 'Low match';
  matchPercent: number | null;
  whyConsider: string[];
}

interface Cohort {
  combinationKey: string;
  sufficient: boolean;
  insufficientReason: string | null;
  cohortSize: number;
  minCohort: number;
  windowDays: number;
  goalAttainment: {
    rate: number | null;
    measured: number;
    attained: number;
    reason: string | null;
    definition: string | null;
    avgDaysToAttain: number | null;
  };
  outcomes: {
    avgWeightChange: number | null;
    pctWeightChange: number | null;
    meanA1cChange: number | null;
    glycemicImprovedPct: number | null;
    continuationPct: number | null;
  } | null;
  /** Muscle Growth / Strength pathway only, clinician only. */
  treatmentMatch?: { score: number; components: Record<string, number | null> } | null;
}

interface Preview {
  patientId: string;
  firstName: string;
  goalLabel: string | null;
  primaryGoal: string | null;
  /** False for a goal with no number, like "Build lean muscle". */
  goalHasNumericTarget: boolean;
  treatments: Array<{ id: string; therapyId: string; name: string; route: string | null }>;
  cohort: Cohort;
  message: string;
  comparison: { treatmentName: string; rate: number; deltaPoints: number } | null;
}

interface CatalogueTreatment {
  id: string;
  therapyId: string;
  name: string;
  category: string;
  class: string | null;
  route: string | null;
  useOrIndication: string | null;
}

export type WorkspaceTab = 'plan' | 'labs' | 'twin' | 'history' | 'notes' | 'opr' | 'pathway' | 'checkins' | 'mcos';

type TabEntry = { key: WorkspaceTab; label: string; badge?: string; count?: number };

/**
 * The tab set of an HRT / TRT patient ("HRT-TRT Patient View - screenshot.png"). A patient who is also on the
 * weight and peptides program keeps that plan as a tab of its own, so neither plan is out of reach.
 */
function hrtTabs(alsoWeightProgram: boolean, openAlerts: number): TabEntry[] {
  return [
    { key: 'plan', label: 'HRT / TRT Plan' },
    ...(alsoWeightProgram ? [{ key: 'mcos' as const, label: 'Weight & Peptides Plan' }] : []),
    { key: 'pathway', label: 'Clinical Pathway' },
    { key: 'labs', label: 'Labs & Monitoring' },
    { key: 'twin', label: 'Clinical Twin Insights' },
    { key: 'checkins', label: 'Check-ins', count: openAlerts },
    { key: 'history', label: 'Patient History' },
    { key: 'notes', label: 'Notes' },
    { key: 'opr', label: 'OPR Pricing' },
  ];
}

const TABS: TabEntry[] = [
  { key: 'plan', label: 'MCOS Treatment Plan' },
  { key: 'labs', label: 'Lab Results' },
  { key: 'twin', label: 'Clinical Twin Insights' },
  { key: 'history', label: 'Patient History' },
  { key: 'notes', label: 'Notes' },
  // Board 01: the sixth tab, after Notes, flagged NEW.
  { key: 'opr', label: 'OPR Pricing', badge: 'NEW' },
];

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

function heightLabel(inches: number | null): string | null {
  if (!inches) return null;
  const feet = Math.floor(inches / 12);
  const rest = Math.round(inches - feet * 12);
  return `${feet}’${rest}”`;
}

export function demographicsLine(patient: WorkspaceData['patient']): string[] {
  return [
    patient.sexAtBirth,
    patient.age !== null ? `Age ${patient.age}` : null,
    heightLabel(patient.heightIn),
    patient.weightLb !== null ? `${Math.round(patient.weightLb)} lbs` : null,
    patient.bmi !== null ? `BMI ${patient.bmi}` : null,
  ].filter((part): part is string => Boolean(part));
}

/** The mockup's row glyph, picked from the library's category and class. */
function TreatmentGlyph({ treatment, size }: { treatment: { name: string; category: string; class: string | null }; size: number }) {
  const vitaminCode = /^B\d{1,2}$/i.test(treatment.name.trim()) ? treatment.name.trim().toUpperCase() : null;
  if (vitaminCode) {
    return <span className="font-display text-forest" style={{ fontSize: size * 0.72, fontWeight: 480 }}>{vitaminCode}</span>;
  }
  const icon = size;
  if (treatment.category === 'GLP-1') return <IconSyringe size={icon} className="text-forest" />;
  if (treatment.category === 'Peptide') {
    return /repair/i.test(treatment.class ?? '') ? (
      <IconBandage size={icon} className="text-forest" />
    ) : (
      <IconMolecule size={icon} className="text-forest" />
    );
  }
  if (treatment.category === 'IM' || treatment.category === 'IV') return <IconLeaf size={icon} className="text-forest" />;
  return <IconPill size={icon} className="text-forest" />;
}

const MATCH_BOX: Record<Recommendation['priorityKey'], string> = {
  highest: 'bg-[#edf5ef] text-[#2b5a50]',
  high: 'bg-[#e8f0fa] text-[#2c4a6b]',
  moderate: 'bg-[#fbecd9] text-[#8a5a24]',
  low: 'bg-[#f1f1ee] text-ink-muted',
};

const MATCH_CHIP: Record<Recommendation['priorityKey'], string> = {
  highest: 'bg-[#e3efe9] text-[#2b5a50]',
  high: 'bg-[#e3efe9] text-[#2b5a50]',
  moderate: 'bg-[#fbecd9] text-[#8a5a24]',
  low: 'bg-[#f1f1ee] text-ink-muted',
};

function StepNumber({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-forest font-display text-[17px] text-white">
      {children}
    </span>
  );
}

function Checkbox({ checked, size = 26 }: { checked: boolean; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={`grid shrink-0 place-items-center rounded-[5px] border ${
        checked ? 'border-forest bg-forest text-white' : 'border-[#b8c4c0] bg-white text-transparent'
      }`}
    >
      <IconCheck size={Math.round(size * 0.62)} />
    </span>
  );
}

function useOutside(ref: React.RefObject<HTMLElement | null>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, close]);
}

/* ------------------------------------------------------------------ *
 * The panel
 * ------------------------------------------------------------------ */

export function PatientWorkspace({
  patientId,
  headerCard,
  backTo = '/patients',
  onPlanSent,
}: {
  patientId: string;
  /**
   * The top-right card slot. Defaults to the patient's three domain scores.
   * A function receives the loaded panel data and a way to open a tab.
   */
  headerCard?: ReactNode | ((data: WorkspaceData, openTab: (tab: WorkspaceTab) => void) => ReactNode);
  backTo?: string;
  /** Called after a plan is sent, with the sentence to confirm it by. */
  onPlanSent?: (outcome: { tone: 'ok' | 'warn'; text: string }) => void;
}) {
  const [params, setParams] = useSearchParams();

  const workspace = useQuery({
    queryKey: ['workspace', patientId],
    queryFn: () => api.get<WorkspaceData>(`/patients/${patientId}/workspace`),
  });

  /* An HRT / TRT patient gets the hormone program's header and tabs. Membership comes from the clinic's program
     list (one cached call for every panel); the full hormone view loads only for a member. If the list cannot be
     read the panel falls back to the usual tabs rather than failing for every patient. */
  const program = useHrtProgram();
  const member = Boolean(program.data?.patients.some((row) => row.patientId === patientId));
  const hrt = useHrtView(patientId, member);
  const hrtMode = member && Boolean(hrt.data?.membership.member);
  const [hrtPick, setHrtPick] = useState<HrtPick | null>(null);
  useEffect(() => setHrtPick(null), [patientId]);

  const tabs = hrtMode && hrt.data ? hrtTabs(Boolean(workspace.data?.scores), hrt.data.status.openAlerts) : TABS;
  const tab = (tabs.some((entry) => entry.key === params.get('tab')) ? params.get('tab') : 'plan') as WorkspaceTab;

  /* The builder's ticked treatments live here, so switching to the Twin tab
     and back keeps the doctor's selection. */
  const [selected, setSelected] = useState<string[] | null>(null);
  useEffect(() => setSelected(null), [patientId]);
  /* A patient already on a plan sees it; "Change plan" opens the builder for a new version. */
  const [changing, setChanging] = useState(false);
  useEffect(() => setChanging(false), [patientId]);

  /* Brian's even grid when the panel has room (.ws-tabs in index.css); narrower, the tabs scroll with full labels. Keep the open one in view. */
  const tabList = useRef<HTMLDivElement>(null);
  // The tab row draws once the hormone view is in too, for an HRT / TRT patient.
  const loaded = Boolean(workspace.data) && !program.isLoading && (!member || Boolean(hrt.data));
  useEffect(() => {
    const list = tabList.current;
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !active || list.scrollWidth <= list.clientWidth) return;
    const listBox = list.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    list.scrollLeft += box.left - listBox.left - (listBox.width - box.width) / 2;
  }, [tab, loaded]);

  const setTab = (next: WorkspaceTab) => {
    const copy = new URLSearchParams(params);
    if (next === 'plan') copy.delete('tab');
    else copy.set('tab', next);
    setParams(copy, { replace: true });
  };

  if (workspace.error || (member && hrt.error)) {
    const failed = workspace.error ?? hrt.error;
    return (
      <PanelFrame>
        <ErrorState
          error={failed}
          retry={() => {
            if (workspace.error) void workspace.refetch();
            if (hrt.error) void hrt.refetch();
          }}
        />
      </PanelFrame>
    );
  }
  if (!workspace.data || program.isLoading || (member && !hrt.data)) {
    return (
      <PanelFrame>
        <LoadingRows rows={8} />
      </PanelFrame>
    );
  }

  const data = workspace.data;
  const { patient } = data;
  const hrtView = hrtMode ? hrt.data! : null;
  // The hormone view reads weight and BMI from the intake or the latest check-in when no InBody scan has them.
  const shown = hrtView
    ? {
        ...patient,
        sexAtBirth: hrtView.patient.sexAtBirth ?? patient.sexAtBirth,
        age: hrtView.patient.age ?? patient.age,
        heightIn: hrtView.patient.heightIn ?? patient.heightIn,
        weightLb: patient.weightLb ?? hrtView.patient.weightLb,
        bmi: patient.bmi ?? hrtView.patient.bmi,
      }
    : patient;

  return (
    <PanelFrame>
      {/* --- top row ---------------------------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={backTo} className="inline-flex items-center gap-3 text-[15px] text-navy hover:text-forest">
          <IconArrowLeft size={20} />
          Back to Patient List
        </Link>
        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-4">
          <Link
            to={`/patients/${patient.id}`}
            className="inline-flex h-[35px] items-center whitespace-nowrap rounded-md border border-[#c3d4cf] bg-white px-3 text-[14px] text-navy hover:bg-mint sm:px-5 sm:text-[15px]"
          >
            {/* The full label does not fit beside More Actions on a phone. */}
            <span className="sm:hidden">View full profile</span>
            <span className="hidden sm:inline">View Full Patient Profile</span>
          </Link>
          <MoreActions data={data} onNotes={() => setTab('notes')} />
        </div>
      </div>

      {/* --- who ------------------------------------------------------ */}
      <div className="mt-3 flex flex-wrap items-start gap-x-5 gap-y-4">
        <Avatar
          firstName={patient.firstName}
          lastName={patient.lastName}
          size="xl"
          tone="mint"
          className={`mt-1.5 !h-14 !w-14 !text-[22px] sm:!h-[80px] sm:!w-[80px] sm:!text-[31px] ${hrtView ? '!bg-plum-soft !text-plum-ink' : ''}`}
        />
        <div className="min-w-0 flex-1">
          <h2 className="break-words font-display text-[clamp(26px,7.5vw,34px)] leading-[1.1] text-navy sm:text-[34px]" style={{ fontWeight: 560 }}>
            {patient.firstName} {patient.lastName}
          </h2>
          {/* On a phone a bullet that wraps to the start of a line is pushed out of view. */}
          <div className="max-sm:overflow-hidden">
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[15px] text-[#4d6072] max-sm:-ml-5 max-sm:gap-x-0 sm:text-[17px]">
              {demographicsLine(shown).map((part, index) => (
                <span key={part} className="inline-flex items-center gap-3 max-sm:gap-0">
                  <span aria-hidden="true" className={`text-[12px] text-[#8a97a3] max-sm:w-5 max-sm:text-center ${index === 0 ? 'sm:hidden' : ''}`}>•</span>
                  {part}
                </span>
              ))}
            </p>
          </div>
          {hrtView ? (
            /* The hormone program box and the engine's chips under it, as drawn for the HRT / TRT patient view. */
            <div className="mt-3">
              <HrtGoalBox trackLabel={hrtView.membership.trackLabel} goal={hrtView.patient.goal.display ?? patient.goalHeadline} />
              <HrtSummaryChips chips={hrtView.summaryChips} />
            </div>
          ) : (
            <div className="ws-goal-row mt-3 flex items-center gap-2.5">
              <GoalBox headline={patient.goalHeadline} />
              {/* Stacked beside the goal when the panel has room (.ws-goal-row in index.css): the engine's phenotype names
                  are longer than the mockup's chips. A chip is never wider than its column, so it never runs under the scores card. */}
              <div className="flex min-w-0 flex-wrap content-center items-center gap-1.5">
                {patient.phenotypes.map((phenotype) => (
                  <span key={phenotype.tag} title={phenotype.label} className="max-w-full truncate whitespace-nowrap rounded-[5px] bg-[#e7eef7] px-[7px] py-[5px] text-[11.5px] leading-none text-navy">
                    {phenotype.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="w-full sm:w-[250px] shrink-0">
          {hrtView ? (
            <HrtStatusCard view={hrtView} onOpenLabs={() => setTab('labs')} onOpenCheckins={() => setTab('checkins')} />
          ) : typeof headerCard === 'function' ? (
            headerCard(data, setTab)
          ) : (
            (headerCard ?? <DomainScoresCard scores={data.scores} />)
          )}
        </div>
      </div>

      {/* --- tabs ----------------------------------------------------- */}
      {/* The hormone set has eight tabs: they wrap into two rows as drawn, and scroll on a phone like the usual six. */}
      <div
        ref={tabList}
        role="tablist"
        className={
          hrtView
            ? 'no-scrollbar mt-3 flex gap-[2px] overflow-x-auto rounded-md bg-[#eef2f1] p-[3px] text-[15px] sm:flex-wrap sm:overflow-visible'
            : 'ws-tabs no-scrollbar mt-3 flex gap-[2px] overflow-x-auto rounded-md bg-[#eef2f1] p-0 text-[15px]'
        }
        data-testid="workspace-tabs"
      >
        {tabs.map((entry) => (
          <button
            key={entry.key}
            role="tab"
            type="button"
            aria-selected={tab === entry.key}
            onClick={() => setTab(entry.key)}
            className={`h-[35px] shrink-0 whitespace-nowrap rounded-md px-3 transition-colors ${hrtView ? 'sm:flex-auto' : 'flex-1'} ${
              tab === entry.key ? 'bg-forest text-white' : 'bg-[#eef2f1] text-navy hover:bg-[#e3e9e7]'
            }`}
          >
            {entry.label}
            {entry.badge && (
              <span className="ml-1.5 rounded-[5px] bg-[#fff1d6] px-1.5 py-0.5 text-[11px] font-semibold text-[#7a4108]">{entry.badge}</span>
            )}
            {entry.count ? (
              <span
                aria-label={`${entry.count} open`}
                className={`ml-1.5 inline-grid h-[19px] min-w-[19px] place-items-center rounded-full px-1 text-[11px] font-semibold leading-none ${
                  tab === entry.key ? 'bg-white text-alert' : 'bg-alert text-white'
                }`}
              >
                {entry.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div key={tab} className="mcos-fade mt-3">
        {/* S-26: a protocol change that touches this patient, answered here. */}
        {tab === 'plan' && <ProtocolNotices patientId={patient.id} onRevise={() => setChanging(true)} />}
        {(hrtView ? tab === 'mcos' : tab === 'plan') && <MicrodoseDoseCard patientId={patient.id} />}
        {tab === 'plan' && hrtView && (
          <HrtPlanTab
            view={hrtView}
            pick={hrtPick}
            setPick={setHrtPick}
            onOpenOpr={() => setTab('opr')}
            onOpenTab={(next) => setTab(next)}
            onPlanSent={(outcome) => onPlanSent?.(outcome)}
          />
        )}
        {/* An HRT / TRT patient on the weight and peptides program too keeps that builder on its own tab. */}
        {(hrtView ? tab === 'mcos' : tab === 'plan') &&
          (data.activeRegimen && !changing ? (
            <CurrentPlan regimen={data.activeRegimen} patientId={patient.id} onChange={() => setChanging(true)} />
          ) : (
            <>
              <PlanStatus patientId={patient.id} />
              <TreatmentBuilder
                data={data}
                selected={selected}
                setSelected={setSelected}
                onOpenOpr={() => setTab('opr')}
                onPlanSent={(outcome) => {
                  setChanging(false);
                  onPlanSent?.(outcome);
                }}
              />
            </>
          ))}
        {tab === 'labs' && hrtView && <HrtLabsTab view={hrtView} />}
        {tab === 'labs' && (!hrtView || data.labs) && (
          <div className={hrtView ? 'mt-2.5' : undefined}>
            <LabsTab data={data} />
          </div>
        )}
        {tab === 'labs' && <BodyScanPanel patientId={patient.id} />}
        {tab === 'pathway' && hrtView && <HrtPathwayTab view={hrtView} />}
        {tab === 'twin' && (hrtView ? <HrtTwinTab view={hrtView} /> : <TwinTab data={data} selected={selected} />)}
        {tab === 'checkins' && hrtView && <HrtCheckinsTab view={hrtView} />}
        {tab === 'history' && <HistoryTab data={data} />}
        {tab === 'notes' && <NotesTab patientId={patient.id} />}
        {tab === 'opr' &&
          (hrtView ? (
            <HrtOprPricing view={hrtView} pick={hrtPick} firstName={patient.firstName} lastName={patient.lastName} onOpenPlan={() => setTab('plan')} />
          ) : (
            <OprPricingTab patientId={patient.id} firstName={patient.firstName} lastName={patient.lastName} selected={selected} />
          ))}
      </div>
    </PanelFrame>
  );
}

function PanelFrame({ children }: { children: ReactNode }) {
  return (
    <section className="ws-panel rounded-[14px] border border-hairline bg-[#fdfdfb] px-3 pt-2.5 pb-3 sm:px-3.5">
      {children}
    </section>
  );
}

/* --- the red goal box, as drawn ---------------------------------------- */

export function GoalBox({ headline }: { headline: string | null }) {
  if (!headline) {
    return (
      <span className="inline-flex h-[58px] items-center rounded-lg border border-dashed border-[#e6b8b8] px-4 text-[14px] text-[#9a3b3b]">
        No goal recorded yet
      </span>
    );
  }
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-3 rounded-lg bg-gradient-to-r from-[#ffd9da] to-[#ffe6e6] pl-2.5 pr-4 py-1.5 sm:max-w-none sm:shrink-0 sm:whitespace-nowrap">
      <span className="flex shrink-0"><TargetMark size={44} /></span>
      <span className="min-w-0 leading-none">
        <span className="block text-[13px] font-medium uppercase tracking-[0.03em] text-alert sm:text-[14.5px]">Primary goal</span>
        <span className="mt-1 block break-words font-display text-[clamp(24px,7vw,34px)] leading-[1.05] text-alert sm:text-[34px]" style={{ fontWeight: 600 }}>
          {headline}
        </span>
      </span>
    </span>
  );
}

/** The filled red bullseye with an arrow, from the goal box. */
export function TargetMark({ size = 46 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="22" cy="26" r="17" fill="none" stroke="var(--alert-red)" strokeWidth="5" />
      <circle cx="22" cy="26" r="9.5" fill="none" stroke="var(--alert-red)" strokeWidth="5" />
      <circle cx="22" cy="26" r="3.2" fill="var(--alert-red)" />
      <path d="M22 26 38 10" stroke="var(--alert-red)" strokeWidth="3.2" strokeLinecap="round" />
      <path d="M33 7.5 38.5 9.5 40.5 15" fill="none" stroke="var(--alert-red)" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* --- the top-right card when it is not the alerts ------------------------ */

export function DomainScoresCard({ scores }: { scores: WorkspaceData['scores'] }) {
  return (
    <div className="h-full rounded-[10px] border border-hairline bg-white px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-mint text-forest">
          <IconTrend size={20} />
        </span>
        <p className="text-[15px] text-navy">MCOS Scores</p>
      </div>
      {scores ? (
        <div className="mt-2 grid grid-cols-3 gap-2">
          {(
            [
              ['Metabolic', scores.metabolic],
              ['Inflammation', scores.inflammation],
              ['Liver', scores.liver],
            ] as const
          ).map(([label, value]) => (
            <div key={label}>
              <p className="font-display text-[26px] leading-none text-navy">{value ?? '-'}</p>
              <p className="mt-1 text-[11.5px] text-[#4d6072]">{label}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-[13.5px] text-[#4d6072]">No scored report yet</p>
      )}
    </div>
  );
}

/* --- Patient status ----------------------------------------------------- */

/** "Dec 5", with the year only when it is not this year's. */
function shortDate(value: string): string {
  const date = new Date(value);
  const label = formatDate(value);
  return date.getUTCFullYear() === new Date().getUTCFullYear() ? label.replace(/, \d{4}$/, '') : label;
}

/**
 * The dashboard's top-right box once Physician Alerts moved to the header.
 * Brian, 26 September 2026: "That frees up the alerts section currently in
 * the patient treatment plan; please replace it with something useful
 * there." Where this patient is in the six month cycle, the next blood draw,
 * the last RETAIN check-in and their own open lab alerts. Every value is
 * read by `services/patient-status.ts`; a missing one says so in words.
 */
export function PatientStatusCard({ data, onOpenLabs }: { data: WorkspaceData; onOpenLabs: () => void }) {
  const { programme, nextDraw, drawsComplete, retain, openLabAlerts } = data.status;

  const programmeText = !programme
    ? 'Not enrolled'
    : programme.subscriptionStatus === 'CANCELED'
      ? 'Cancelled'
      : programme.complete
        ? `Cycle complete (${programme.cycleMonths} months)`
        : `Month ${programme.month} of ${programme.cycleMonths}`;

  const drawText = nextDraw
    ? `${nextDraw.label}${nextDraw.dueAt ? `, ${shortDate(nextDraw.dueAt)}` : ', date not set'}`
    : drawsComplete
      ? 'All three collected'
      : 'Not scheduled';
  const drawTitle = nextDraw
    ? nextDraw.source === 'order'
      ? 'Date from the lab order'
      : 'Due date from the monitoring plan: baseline, month 3, month 6'
    : undefined;

  const tier = retain ? TIER_STYLE[retain.tier] : null;

  const rows: Array<{ label: string; value: ReactNode; title?: string; muted?: boolean }> = [
    { label: 'Programme', value: programmeText, muted: !programme },
    { label: 'Next draw', value: drawText, title: drawTitle, muted: !nextDraw && !drawsComplete },
    {
      label: 'RETAIN',
      value: retain ? (
        <>
          <span style={{ color: tier?.text }}>
            {retain.score !== null ? `${retain.score}, ` : ''}
            {retain.tierLabel}
          </span>
          <span className="text-[#4d6072]">, {shortDate(retain.assessedAt)}</span>
        </>
      ) : (
        'No check in yet'
      ),
      title: retain ? `Month ${retain.month} check in` : undefined,
      muted: !retain,
    },
  ];

  return (
    <div className="h-full rounded-[10px] border border-hairline bg-white px-3.5 py-2.5">
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-mint text-forest">
          <IconCalendar size={17} />
        </span>
        <p className="text-[15px] text-navy">Patient status</p>
      </div>
      <dl className="mt-1.5 space-y-[3px] text-[13px] leading-[18px]">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-[#4d6072]">{row.label}</dt>
            <dd title={row.title} className={`min-w-0 truncate text-right ${row.muted ? 'text-[#6b7a88]' : 'font-medium text-navy'}`}>
              {row.value}
            </dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3">
          <dt className="shrink-0 text-[#4d6072]">Lab alerts</dt>
          <dd className="min-w-0 text-right">
            <button
              type="button"
              onClick={onOpenLabs}
              className={`underline-offset-2 hover:underline ${openLabAlerts > 0 ? 'font-semibold text-alert' : 'text-forest'}`}
            >
              {openLabAlerts > 0 ? `${openLabAlerts} open` : 'None open'}
            </button>
          </dd>
        </div>
      </dl>
    </div>
  );
}

/* --- More actions ------------------------------------------------------- */

function MoreActions({ data, onNotes }: { data: WorkspaceData; onNotes: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useOutside(box, open, () => setOpen(false));
  const id = data.patient.id;
  const items: Array<{ label: string; to?: string; onClick?: () => void }> = [
    ...(data.labs ? [{ label: 'Open lab results', to: `/labs/${data.labs.labReportId}` }] : []),
    ...(data.scores ? [{ label: 'View MCOS report', to: `/reports/${data.scores.reportId}` }] : []),
    { label: 'Record body composition', to: `/patients/${id}/inbody` },
    { label: 'Edit patient details', to: `/patients/${id}` },
    { label: 'Add a note', onClick: onNotes },
  ];
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="inline-flex h-[35px] items-center gap-2 whitespace-nowrap rounded-md border border-[#c3d4cf] bg-white pl-3 pr-2 text-[14px] text-navy hover:bg-mint sm:gap-6 sm:pl-4 sm:pr-3 sm:text-[15px]"
      >
        More Actions
        <IconChevronDown size={18} />
      </button>
      {open && (
        <div role="menu" className="mcos-pop absolute right-0 z-30 mt-1.5 w-56 rounded-lg border border-hairline bg-white py-1 shadow-[0_12px_28px_rgba(20,23,28,0.12)]">
          {items.map((item) =>
            item.to ? (
              <Link key={item.label} to={item.to} role="menuitem" className="block px-4 py-2 text-[14px] text-navy hover:bg-mint" onClick={() => setOpen(false)}>
                {item.label}
              </Link>
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                className="block w-full px-4 py-2 text-left text-[14px] text-navy hover:bg-mint"
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Tab 1: the treatment builder, steps 1 to 3
 * ------------------------------------------------------------------ */

function TreatmentBuilder({
  data,
  selected,
  setSelected,
  onOpenOpr,
  onPlanSent,
}: {
  data: WorkspaceData;
  selected: string[] | null;
  setSelected: (next: string[]) => void;
  onOpenOpr: () => void;
  onPlanSent?: (outcome: { tone: 'ok' | 'warn'; text: string }) => void;
}) {
  const queryClient = useQueryClient();
  const patientId = data.patient.id;
  const prices = useOprPrices();
  // Building, checking and sending a plan is the provider's (Roles and Access). Staff logins see the plan, not the builder's checks.
  const provider = useAuth().can('ADMIN', 'CLINICIAN');

  const recommendations = useQuery({
    queryKey: ['recommendations', patientId],
    queryFn: () => api.get<{ recommendations: Recommendation[] }>(`/patients/${patientId}/recommendations`),
  });
  const catalogue = useQuery({
    queryKey: ['treatments'],
    queryFn: () => api.get<{ treatments: CatalogueTreatment[] }>('/treatments'),
    staleTime: 60 * 60 * 1000,
  });

  const recs = recommendations.data?.recommendations ?? [];
  const recommended = recs.filter((entry) => entry.recommended);
  const others = recs.filter((entry) => !entry.recommended);

  /*
   * The starting selection. A saved draft wins, because it is what a doctor
   * already chose; otherwise the top three are ticked, as the mockup shows.
   * Nothing is sent from this: the doctor still presses Send, and unticking
   * redraws the prediction immediately.
   */
  useEffect(() => {
    if (selected !== null || !recommendations.data) return;
    // An unsent selection from this tab wins: a refresh or a sign-in again must not lose a half-built plan.
    const unsent = readBuilderDraft(patientId);
    if (unsent) {
      setSelected(unsent.selected);
      if (unsent.message) setRestoredMessage(unsent.message);
      setRestored(true);
      return;
    }
    const draft = data.draftRegimen?.treatments.map((row) => row.treatmentId) ?? [];
    const active = data.activeRegimen?.treatments.map((row) => row.treatmentId) ?? [];
    const start = draft.length > 0 ? draft : active.length > 0 ? active : recommended.map((entry) => entry.treatmentId);
    startKey.current = [...start].sort().join(',');
    setSelected(start);
  }, [selected, recommendations.data, data.draftRegimen, data.activeRegimen, recommended, setSelected, patientId]);
  /** The selection the builder opened with; nothing is autosaved until the clinician changes it. */
  const startKey = useRef<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [restoredMessage, setRestoredMessage] = useState<string | null>(null);

  const current = selected ?? [];
  const selectionKey = [...current].sort().join(',');

  /* Board 01: a price on every ticked drug before the plan can go. */
  const pricedItems = current.flatMap((treatmentId) => prices.byTreatment.get(treatmentId) ?? []);
  const withoutPriceRow = current.length - pricedItems.length;
  const allPriced = current.length > 0 && withoutPriceRow === 0 && pricingBanner(bannerLines(pricedItems)).allSet;
  const priceBoxFor = (treatmentId: string) => {
    const item = prices.byTreatment.get(treatmentId);
    return item ? (
      <PriceBox item={item} onSave={(change) => prices.save(item.itemId, change)} onDraft={(cents) => prices.setDraft(item.itemId, cents)} />
    ) : null;
  };
  const toggleOnCard = (treatmentId: string) => (event: React.MouseEvent) => {
    // The price box is a control of its own; typing or pressing Use never ticks the card.
    if ((event.target as HTMLElement).closest('[data-price-box]')) return;
    toggle(treatmentId);
  };

  const preview = useQuery({
    queryKey: ['treatment-preview', patientId, selectionKey],
    queryFn: () => api.get<Preview>(`/regimens/preview${qs({ patientId, treatments: selectionKey })}`),
    enabled: provider && selected !== null,
  });

  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [signingPlanId, setSigningPlanId] = useState<string | null>(null);
  const [whyOpen, setWhyOpen] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const [showAll, setShowAll] = useState(false);

  /* S-22: the sign-off checks on the ticked drugs, live, so nothing surprises the prescriber at the end (SG-003). */
  const signoff = useQuery({
    queryKey: ['signoff-preview', patientId, selectionKey],
    queryFn: () =>
      api.post<SignoffView>('/signoff/preview', { patientId, treatments: current.map((treatmentId) => ({ treatmentId, dose: null })) }),
    enabled: provider && selected !== null && current.length > 0 && allPriced,
  });

  useEffect(() => {
    // A restored edit belongs to the restored selection; any other change of ticks starts the message afresh.
    if (restoredMessage !== null) {
      setMessage(restoredMessage);
      setRestoredMessage(null);
      return;
    }
    setMessage(null);
    setEditing(false);
  }, [selectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Autosave of the unsent builder, in this tab only. */
  useEffect(() => {
    if (selected === null) return;
    if (selectionKey === startKey.current && message === null) return clearBuilderDraft(patientId);
    writeBuilderDraft(patientId, { selected: current, message });
  }, [patientId, selectionKey, message]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setOutcome(null), [patientId]);

  const release = useMutation({
    mutationFn: (send: boolean) =>
      api.post<{ planId: string; messageSent: boolean; messageError: string | null }>('/regimens/release', {
        patientId,
        treatments: current.map((treatmentId) => ({ treatmentId, dose: null })),
        message: message ?? null,
        send,
      }),
    onSuccess: (result, send) => {
      void queryClient.invalidateQueries({ queryKey: ['treatment-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['workspace', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['opr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['nav-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      clearBuilderDraft(patientId);
      setRestored(false);
      if (!send) {
        setOutcome({ tone: 'ok', text: 'Saved as a draft. Nothing was sent to the patient.' });
        return;
      }
      // Nothing reaches the patient until the plan is signed (SG-001): the drawer takes it from here.
      setOutcome(null);
      setSigningPlanId(result.planId);
    },
    onError: (caught) => {
      setOutcome({ tone: 'error', text: caught instanceof Error ? caught.message : 'Could not save the plan' });
    },
  });

  const toggle = (treatmentId: string) =>
    setSelected(current.includes(treatmentId) ? current.filter((id) => id !== treatmentId) : [...current, treatmentId]);

  const remaining = (catalogue.data?.treatments ?? []).filter(
    (treatment) => !recs.some((entry) => entry.treatmentId === treatment.id),
  );

  const cohort = preview.data?.cohort ?? null;
  const body = message ?? preview.data?.message ?? '';

  return (
    <>
      {restored && (
        <p className="mb-2.5 flex flex-wrap items-center justify-between gap-2 rounded-md bg-mint px-3 py-2 text-[13px] text-forest" role="status" data-testid="builder-restored">
          Your unsent plan was restored.
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => {
              clearBuilderDraft(patientId);
              setRestored(false);
              const fresh = recommended.map((entry) => entry.treatmentId);
              startKey.current = [...fresh].sort().join(',');
              setSelected(fresh);
              setMessage(null);
            }}
          >
            Start over
          </button>
        </p>
      )}
      <RecoveryCard patientId={patientId} />
      <div className="ws-split-plan grid grid-cols-1 gap-2.5">
        {/* ---- Step 1 --------------------------------------------------- */}
        {/* Relative on a phone so the "Why these peptides" panel spans the card rather than its button. */}
        <section className="rounded-[10px] border border-hairline bg-white px-3 pt-2.5 pb-2 max-sm:relative">
          <div className="flex items-start gap-3.5">
            <StepNumber>1</StepNumber>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <h3 className="font-display text-[20px] leading-tight text-navy">Select Treatment Plan</h3>
                <WhyPopover recommendations={recommended} />
              </div>
              <p className="mt-1 text-[13.5px] leading-snug text-[#4d6072]">
                Choose one or more from the matches from the MCOS protocol library, then set the monthly price for each. The faded number is the OPR price.
              </p>
            </div>
          </div>

          {recommendations.isLoading ? (
            <div className="mt-3"><LoadingRows rows={3} /></div>
          ) : recommended.length === 0 ? (
            <p className="mt-3 rounded-lg border border-dashed border-hairline px-4 py-5 text-[14px] text-[#4d6072]">
              This patient's report has no protocol match yet. Choose from all treatments below.
            </p>
          ) : (
            <ul className="mcos-stagger mt-2.5 space-y-2">
              {recommended.map((entry) => {
                const ticked = current.includes(entry.treatmentId);
                return (
                  <li
                    key={entry.treatmentId}
                    onClick={toggleOnCard(entry.treatmentId)}
                    className={`flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-2.5 py-2 transition-colors sm:gap-4 ${
                      ticked ? 'border-[#cfdcd7] bg-white' : 'border-hairline bg-white hover:bg-paper'
                    }`}
                  >
                    {/* The tick area is the button; the price box beside it is a control of its own. */}
                    <button type="button" aria-pressed={ticked} className="flex min-w-0 basis-full items-center gap-3 text-left sm:min-w-[240px] sm:flex-1 sm:basis-0 sm:gap-4">
                      <Checkbox checked={ticked} />
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#eef4f1] sm:h-[54px] sm:w-[54px]">
                        <TreatmentGlyph treatment={entry} size={26} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="font-display text-[17px] text-navy" style={{ fontWeight: 560 }}>{entry.name}</span>
                          {/* WT-02: the basis of the fit, so the prescriber reviews it independently. */}
                          <span
                            role="button"
                            tabIndex={0}
                            className="text-[12.5px] font-semibold text-forest underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              setWhyOpen(whyOpen === entry.treatmentId ? null : entry.treatmentId);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                event.stopPropagation();
                                setWhyOpen(whyOpen === entry.treatmentId ? null : entry.treatmentId);
                              }
                            }}
                          >
                            Why?
                          </span>
                          {prices.byTreatment.get(entry.treatmentId)?.needsPrescriberJustification && (
                            <span className="rounded-md bg-[#fbecd2] px-2 py-[2px] text-[12px] font-semibold text-[#8a5a14]">Allowed with limits: reason needed</span>
                          )}
                        </span>
                        {whyOpen === entry.treatmentId && (
                          <span className="mt-1 block rounded-md bg-[#f2f6f4] px-2.5 py-1.5 text-[12.5px] leading-snug text-[#4d6072]" data-testid={`why-${entry.treatmentId}`}>
                            {(entry.whyConsider.length ? entry.whyConsider : [entry.useOrIndication ?? 'A match from the MCOS protocol library.']).join(' ')}
                          </span>
                        )}
                        {entry.whyConsider[0] && (
                          <span title={entry.whyConsider[0]} className="mt-0.5 block text-[13.5px] leading-snug text-[#4d6072] sm:truncate">
                            {entry.whyConsider[0]}
                          </span>
                        )}
                        {entry.useOrIndication && (
                          <span title={entry.useOrIndication} className="block text-[13.5px] leading-snug text-[#4d6072] sm:truncate">
                            {entry.useOrIndication}
                          </span>
                        )}
                      </span>
                    </button>
                    {priceBoxFor(entry.treatmentId)}
                    <span className={`flex items-baseline gap-2 rounded-md px-3 py-1 text-center sm:grid sm:w-[112px] sm:shrink-0 sm:place-items-center sm:gap-0 sm:px-2 sm:py-2 ${MATCH_BOX[entry.priorityKey]}`}>
                      <span className="text-[15px] leading-tight">{protocolFitLabel(entry.matchLabel, entry.matchPercent)}</span>
                      {entry.matchPercent !== null && (
                        <span className="font-display text-[19px] leading-tight sm:text-[23px]" style={{ fontWeight: 600 }}>
                          {entry.matchPercent}%
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {(others.length > 0 || remaining.length > 0) && (
            <div className="mt-2.5">
              <p className="font-display text-[15.5px] text-navy">Other Options (optional)</p>
              {others.length > 0 && (
                <ul className="mt-1.5 divide-y divide-hairline/70 rounded-lg border border-hairline">
                  {others.map((entry) => (
                    <OptionRow
                      key={entry.treatmentId}
                      name={entry.name}
                      description={entry.useOrIndication}
                      glyph={entry}
                      ticked={current.includes(entry.treatmentId)}
                      onToggle={() => toggle(entry.treatmentId)}
                      price={priceBoxFor(entry.treatmentId)}
                      chip={
                        <span className={`inline-block whitespace-nowrap rounded-[7px] px-2.5 py-1.5 text-[14px] ${MATCH_CHIP[entry.priorityKey]}`}>
                          Protocol fit
                          {entry.matchPercent !== null ? <b className="ml-1 font-semibold">{entry.matchPercent}%</b> : <b className="ml-1 font-semibold">{fitWord(entry.matchLabel)}</b>}
                        </span>
                      }
                    />
                  ))}
                </ul>
              )}
              {remaining.length > 0 && (
                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={() => setShowAll((was) => !was)}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-forest"
                  >
                    <IconChevronDown size={15} className={`transition-transform ${showAll ? 'rotate-180' : ''}`} />
                    {showAll ? 'Hide other treatments' : `All other treatments (${remaining.length})`}
                  </button>
                  {showAll && (
                    <ul className="mt-1.5 max-h-64 overflow-y-auto divide-y divide-hairline/70 rounded-lg border border-hairline">
                      {remaining.map((treatment) => (
                        <OptionRow
                          key={treatment.id}
                          name={treatment.name}
                          description={treatment.useOrIndication}
                          glyph={treatment}
                          ticked={current.includes(treatment.id)}
                          onToggle={() => toggle(treatment.id)}
                          price={priceBoxFor(treatment.id)}
                          chip={<span className="text-[12px] text-ink-muted">Not a protocol match for this patient</span>}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* S-02: the banner, directly under the list. */}
          {current.length > 0 && (
            <PricingBanner items={pricedItems} unpriced={withoutPriceRow} firstName={data.patient.firstName} onOpenOpr={onOpenOpr} />
          )}
          {prices.saveError && (
            <p role="alert" className="mt-2 rounded-md bg-alert-soft px-3 py-2 text-[13px] text-alert">
              {prices.saveError}
            </p>
          )}
        </section>

        {/* ---- Step 2 --------------------------------------------------- */}
        <section className="rounded-[10px] border border-hairline bg-white px-3 pt-2.5 pb-3">
          <div className="flex items-start gap-3.5">
            <StepNumber>2</StepNumber>
            <div className="min-w-0">
              <h3 className="font-display text-[20px] leading-tight text-navy">Clinical Twin Goal Prediction</h3>
              <p className="mt-1 text-[13.5px] leading-snug text-[#4d6072]">{twinSubtitle(cohort, data.patient.primaryGoal, current.length)}</p>
            </div>
          </div>
          <TwinPrediction cohort={cohort} loading={preview.isFetching && !cohort} goal={data.patient.goalHeadline} />
          <TwinMatchCard match={cohort?.treatmentMatch} />
          {data.patient.primaryGoal && preview.data && !preview.data.goalHasNumericTarget && (
            <p className="mt-3 text-[13.5px] leading-snug text-[#4d6072]" data-testid="no-numeric-target">
              {data.patient.firstName}'s plan page will say there is no numeric target yet, and will not show a time estimate.
            </p>
          )}
          {preview.data?.comparison && (
            <div className="mt-3 flex items-center gap-5 rounded-[10px] bg-[#e8f2ed] px-4 py-3">
              <IconTrend size={34} className="shrink-0 text-forest" />
              <div>
                <p className="font-display text-[18.5px] leading-tight text-navy" style={{ fontWeight: 560 }}>
                  {preview.data.comparison.deltaPoints >= 0 ? '+' : ''}
                  {preview.data.comparison.deltaPoints} percentage points
                </p>
                <p className="text-[15px] text-[#4d6072]">vs. {preview.data.comparison.treatmentName} alone</p>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="ws-split-send mt-2.5 grid grid-cols-1 gap-2.5">
        {/* ---- Step 3 --------------------------------------------------- */}
        <section className="rounded-[10px] border border-hairline bg-white px-3 pt-2.5 pb-3">
          <div className="flex items-start gap-3.5">
            <StepNumber>3</StepNumber>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div>
                  <h3 className="font-display text-[20px] leading-tight text-navy">Preview Patient Message</h3>
                  <p className="mt-0.5 text-[12.5px] text-[#6b7a88]">
                    This message will be sent to {data.patient.firstName}. You can edit it before sending.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={current.length === 0}
                  onClick={() => setEditing((was) => !was)}
                  className="inline-flex h-[30px] shrink-0 items-center gap-2 rounded-md border border-[#c3d4cf] bg-white px-3.5 text-[14px] text-navy hover:bg-mint disabled:opacity-50"
                >
                  <IconPencil size={16} />
                  {editing ? 'Done editing' : 'Edit Message'}
                </button>
              </div>
            </div>
          </div>
          {current.length === 0 ? (
            <p className="mt-3 rounded-lg bg-[#eef2f7] px-4 py-6 text-center text-[13.5px] text-[#4d6072]">
              Tick at least one treatment to see the message.
            </p>
          ) : editing ? (
            <textarea
              value={body}
              onChange={(event) => setMessage(event.target.value)}
              className="mt-3 min-h-[190px] w-full rounded-lg border border-[#c8d3de] bg-[#f5f8fb] px-4 py-3 text-[13px] leading-relaxed text-navy focus:outline-none focus:border-forest/50"
            />
          ) : (
            <div className="mt-3 space-y-2.5 rounded-lg bg-[#eef2f7] px-4 py-3.5 text-[12.5px] leading-relaxed text-navy">
              {preview.isFetching && !preview.data ? (
                <LoadingRows rows={3} />
              ) : (
                body
                  .split(/\n{2,}/)
                  .map((paragraph) => paragraph.trim())
                  .filter(Boolean)
                  .map((paragraph, index) => <p key={index} className="whitespace-pre-wrap">{paragraph}</p>)
              )}
            </div>
          )}
        </section>

        {/* ---- Send ----------------------------------------------------- */}
        <section className="rounded-[10px] border border-hairline bg-white px-3 pt-2 pb-3">
          {/* Board 15: the live checks and Review and sign in one card; board 1 until every price is set. */}
          {!provider ? (
            <p className="py-2 text-center text-[14px] text-[#4d6072]">Only a provider builds, signs and sends a treatment plan.</p>
          ) : allPriced && signoff.data ? (
            <SignBanner
              banner={signoff.data.banner}
              buttonLabel={signoff.data.route.mode === 'send_to_sign' ? signoff.data.route.buttonLabel : 'Review and sign'}
              onOpen={() => release.mutate(true)}
              busy={release.isPending}
            />
          ) : (
            <button
              type="button"
              disabled
              className="flex h-[42px] w-full items-center justify-center gap-3 rounded-md bg-forest text-[15px] font-medium text-white disabled:opacity-50"
            >
              <IconSend size={19} />
              {allPriced ? 'Checking the plan' : 'Set every price to send'}
            </button>
          )}
          {provider && <button
            type="button"
            disabled={current.length === 0 || release.isPending}
            onClick={() => release.mutate(false)}
            className="mt-3 flex h-[34px] w-full items-center justify-center gap-3 rounded-md border border-[#c3d4cf] bg-white text-[15px] text-navy hover:bg-mint disabled:opacity-50"
          >
            <IconNote size={18} />
            Save as Draft
          </button>}
          {outcome && (
            <p
              role="status"
              className={`mt-3 rounded-md px-3 py-2 text-[13px] ${
                outcome.tone === 'ok'
                  ? 'bg-mint text-forest'
                  : outcome.tone === 'warn'
                    ? 'bg-risk-yellow-soft text-risk-yellow'
                    : 'bg-alert-soft text-alert'
              }`}
            >
              {outcome.text}
            </p>
          )}
          <p className="mt-3.5 font-display text-[17px] text-navy" style={{ fontWeight: 560 }}>Next Steps</p>
          <ol className="mt-2 space-y-2.5">
            {[
              'Plan signed by the prescriber and sent to the patient',
              'Patient accepts, asks a question, or says not right now',
              'Accepted plans appear in Ready to Prescribe',
              'Track progress via RETAIN',
            ].map((step, index) => (
              <li key={step} className="flex items-center gap-4 text-[14px] text-[#5b6b7c]">
                <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border border-[#2b5a50] text-[12.5px] text-[#2b5a50]">
                  {index + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </section>
      </div>

      {signingPlanId && (
        <SignDrawer
          planId={signingPlanId}
          patientId={patientId}
          onClose={() => setSigningPlanId(null)}
          onSent={(text, tone) => {
            setOutcome({ tone, text });
            onPlanSent?.({ tone, text });
          }}
        />
      )}
    </>
  );
}

/*
 * Autosave of the treatment builder, per patient, in sessionStorage: it
 * survives a refresh and a sign-in again in the same tab, and is gone when the
 * tab closes, so nothing lingers on a shared computer.
 */
interface BuilderDraft {
  selected: string[];
  message: string | null;
  savedAt: number;
}
const builderKey = (patientId: string) => `mcos-builder-${patientId}`;
const BUILDER_TTL_MS = 12 * 3600 * 1000;

function readBuilderDraft(patientId: string): BuilderDraft | null {
  try {
    const raw = sessionStorage.getItem(builderKey(patientId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as BuilderDraft;
    if (!Array.isArray(draft.selected) || Date.now() - draft.savedAt > BUILDER_TTL_MS) return null;
    return draft;
  } catch {
    return null;
  }
}

function writeBuilderDraft(patientId: string, draft: Omit<BuilderDraft, 'savedAt'>): void {
  try {
    sessionStorage.setItem(builderKey(patientId), JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // Storage blocked: the builder still works, it just cannot be restored.
  }
}

function clearBuilderDraft(patientId: string): void {
  try {
    sessionStorage.removeItem(builderKey(patientId));
  } catch {
    // Nothing to clear.
  }
}

/** WT-02: "Protocol fit", never "High match"; the word only where there is no percent. */
function fitWord(label: string): string {
  return label.replace(/\s*match$/i, '').toLowerCase();
}

function protocolFitLabel(label: string, percent: number | null): string {
  return percent !== null ? 'Protocol fit' : `Protocol fit: ${fitWord(label)}`;
}

function twinSubtitle(cohort: Cohort | null, goal: string | null, selectedCount: number): string {
  if (selectedCount === 0) return 'Tick a treatment to see what happened to patients like this one.';
  if (!cohort) return 'Matching this patient against MCOS outcomes...';
  if (!cohort.sufficient) {
    return cohort.cohortSize > 0
      ? `${cohort.cohortSize} matched ${cohort.cohortSize === 1 ? 'patient has' : 'patients have'} used this exact combination, fewer than the ${cohort.minCohort} MCOS needs before quoting a result.`
      : (cohort.insufficientReason ?? 'Limited MCOS data');
  }
  return `Based on ${cohort.cohortSize.toLocaleString()} matched patients who used this combination${goal ? ` for ${goal}` : ''}.`;
}

function OptionRow({
  name,
  description,
  glyph,
  ticked,
  onToggle,
  chip,
  price,
}: {
  name: string;
  description: string | null;
  glyph: { name: string; category: string; class: string | null };
  ticked: boolean;
  onToggle: () => void;
  chip: ReactNode;
  /** The price box, a sibling of the tick button because an input cannot sit inside one. */
  price: ReactNode;
}) {
  return (
    <li
      onClick={(event) => {
        if (!(event.target as HTMLElement).closest('[data-price-box]')) onToggle();
      }}
      className="flex cursor-pointer flex-wrap items-center gap-x-3.5 gap-y-2 px-4 py-3 hover:bg-paper"
    >
      <button type="button" aria-pressed={ticked} className="flex min-w-0 basis-full items-center gap-3.5 text-left sm:min-w-[200px] sm:flex-1 sm:basis-0">
        <Checkbox checked={ticked} size={22} />
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#eef4f1]">
          <TreatmentGlyph treatment={glyph} size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span title={name} className="block break-words font-display text-[17px] text-navy" style={{ fontWeight: 560 }}>{name}</span>
          {description && (
            <span title={description} className="block text-[13px] leading-snug text-[#5b6168] sm:truncate">{description}</span>
          )}
        </span>
      </button>
      {price}
      <span className="shrink-0">{chip}</span>
    </li>
  );
}

function WhyPopover({ recommendations }: { recommendations: Recommendation[] }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useOutside(box, open, () => setOpen(false));
  return (
    <div ref={box} className="shrink-0 sm:relative">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        className="inline-flex h-[27px] items-center gap-2 rounded-md border border-[#c3d4cf] bg-white px-2.5 text-[12.5px] text-navy hover:bg-mint"
      >
        Why these peptides?
        <IconInfo size={15} />
      </button>
      {open && (
        <div className="mcos-pop absolute right-0 z-30 mt-1.5 w-[340px] rounded-xl border border-hairline bg-white p-4 shadow-[0_14px_36px_rgba(20,23,28,0.14)] max-sm:inset-x-3 max-sm:w-auto">
          <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>Why these peptides</p>
          <p className="mt-1 text-[12.5px] text-[#4d6072]">
            Protocol matches from the MCOS protocol library, from this patient's goals, symptoms, labs and clinical patterns
            on their latest report. A clinical reference, not a recommendation: you decide.
          </p>
          <ul className="mt-3 space-y-3">
            {recommendations.map((entry) => (
              <li key={entry.treatmentId}>
                <p className="text-[13.5px] font-semibold text-navy">
                  {entry.rank}. {entry.name}{' '}
                  <span className="font-normal text-[#4d6072]">
                    · Protocol fit {entry.matchPercent !== null ? `${entry.matchPercent}%` : fitWord(entry.matchLabel)}
                  </span>
                </p>
                <ul className="mt-1 space-y-0.5">
                  {(entry.whyConsider.length > 0 ? entry.whyConsider : [entry.useOrIndication ?? '']).filter(Boolean).map((reason) => (
                    <li key={reason} className="flex gap-2 text-[12.5px] text-[#4d6072]">
                      <IconCheck size={14} className="mt-0.5 shrink-0 text-forest" />
                      {reason}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {recommendations.length === 0 && <li className="text-[13px] text-[#4d6072]">No protocol match on the latest report.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

/* --- step 2's ring and figures ----------------------------------------- */

function AttainmentRing({ rate, size = 140 }: { rate: number; size?: number }) {
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.max(0, Math.min(100, rate)) / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Goal attainment ${rate} percent`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#cfe0da" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--forest)"
        strokeWidth={stroke}
        strokeDasharray={`${filled} ${circumference - filled}`}
        // Draws from empty to the value; the transition re-runs when the
        // clinician changes the combination and the figure moves.
        className="mcos-ring"
        style={{ ['--ring-length' as string]: circumference, transition: 'stroke-dasharray 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

function TwinPrediction({ cohort, loading, goal }: { cohort: Cohort | null; loading: boolean; goal: string | null }) {
  if (loading) return <div className="mt-4"><LoadingRows rows={4} /></div>;
  if (!cohort) return null;
  const rate = cohort.sufficient ? cohort.goalAttainment.rate : null;
  const months = Math.round(cohort.windowDays / 30);
  const weight = cohort.sufficient ? (cohort.outcomes?.avgWeightChange ?? null) : null;
  const toResult = cohort.sufficient ? cohort.goalAttainment.avgDaysToAttain : null;

  const stats: Array<{ icon: ReactNode; value: string; label: string }> = [
    { icon: <IconUsers size={20} />, value: cohort.cohortSize.toLocaleString(), label: 'Matched Patients' },
    {
      icon: <IconScale size={20} />,
      value: weight !== null ? `${weight > 0 ? '+' : ''}${weight} lbs` : 'Limited data',
      label: weight !== null && weight > 0 ? 'Average Weight Gain' : 'Average Weight Loss',
    },
    {
      icon: <IconBars size={20} />,
      value: rate !== null ? `${rate}%` : 'Limited data',
      label: cohort.goalAttainment.definition ? shortDefinition(cohort.goalAttainment.definition) : 'Reached their goal',
    },
    {
      icon: <IconCalendar size={20} />,
      value: toResult !== null ? monthsLabel(toResult) : `${months} months`,
      label: toResult !== null ? 'Average time to key results' : 'Follow-up window',
    },
  ];

  return (
    <div className="mt-3 grid grid-cols-[140px_minmax(0,1fr)] items-center gap-2">
      <div className="relative grid place-items-center">
        {rate !== null ? (
          <>
            <AttainmentRing rate={rate} />
            <div className="absolute inset-0 grid place-items-center text-center">
              <div>
                <p className="font-display text-[32px] leading-none text-navy" style={{ fontWeight: 560 }}>{rate}%</p>
                <p className="mt-1 px-2 text-[12px] leading-tight text-[#4d6072]">
                  of similar patients reached their goal by month {months}
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="grid h-[140px] w-[140px] place-items-center rounded-full border-[12px] border-[#e7eeeb] text-center">
            <p className="px-3 text-[12.5px] leading-snug text-[#4d6072]">
              {goal ? (cohort.goalAttainment.reason ?? cohort.insufficientReason ?? 'Limited MCOS data') : 'No goal recorded'}
            </p>
          </div>
        )}
      </div>
      <ul className="space-y-1">
        {stats.map((stat) => (
          <li key={stat.label} className="flex items-center gap-2.5 rounded-md bg-[#f2f6f4] px-1.5 py-1.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-[#e4efe9] text-forest">{stat.icon}</span>
            <span className="min-w-0">
              <span className="block font-display text-[17px] leading-tight text-navy" style={{ fontWeight: 560 }}>{stat.value}</span>
              <span className="block text-[11px] leading-tight text-[#4d6072]">{stat.label}</span>
            </span>
          </li>
        ))}
      </ul>
      {rate !== null && (
        <p className="col-span-2 text-[12px] leading-snug text-[#4d6072]" data-testid="twin-not-a-prediction">
          In similar patients: {rate}% reached their goal by month {months}. Not a prediction for this patient.
        </p>
      )}
    </div>
  );
}

/** "Reached at least 80% of a 30 lb goal within 6 months" -> "Reached ≥ 80% of 30 lb goal". */
function shortDefinition(definition: string): string {
  return definition.replace(/^Reached at least (\d+)% of a /, 'Reached ≥ $1% of ').replace(/ within \d+ months$/, '');
}

function monthsLabel(days: number): string {
  const months = Math.max(1, Math.round(days / 30.4));
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}

/* --- a patient already on a plan --------------------------------------- */

function CurrentPlan({ regimen, patientId, onChange }: { regimen: RegimenView; patientId: string; onChange: () => void }) {
  return (
    <section className="rounded-[10px] border border-hairline bg-white px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-[20px] text-navy">Current treatment plan</h3>
          <p className="mt-0.5 text-[13.5px] text-[#4d6072]">
            {regimen.startedAt ? `Started ${formatDate(regimen.startedAt)}` : `Chosen ${formatDate(regimen.selectedAt)}`} · {regimen.regimenCode}
          </p>
        </div>
        <button
          type="button"
          onClick={onChange}
          className="inline-flex h-[34px] items-center rounded-md border border-[#c3d4cf] bg-white px-4 text-[14px] text-navy hover:bg-mint"
        >
          Change plan
        </button>
      </div>
      <ul className="mt-3 grid gap-2 sm:grid-cols-3">
        {regimen.treatments.map((row) => (
          <li key={row.treatmentId} className="flex items-center gap-3 rounded-lg border border-hairline px-3 py-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#eef4f1]">
              <TreatmentGlyph treatment={{ name: row.name, category: row.category, class: null }} size={22} />
            </span>
            <span className="min-w-0">
              <span className="block font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>{row.name}</span>
              <span className="block text-[12.5px] text-[#4d6072]">
                {[row.dose, row.frequency].filter(Boolean).join(', ') || row.route || 'Dose not recorded'}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12.5px] text-[#6b7a88]">
        Changing what the patient takes records a new plan, so the outcome of this one stays measurable.
      </p>
      {/* Awaiting patient, Viewed, Accepted or Not right now, with the reason. */}
      <div className="mt-4">
        <PlanStatus patientId={patientId} />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Tab 2: lab results
 * ------------------------------------------------------------------ */

function LabsTab({ data }: { data: WorkspaceData }) {
  if (!data.labs) {
    return <Empty title="No scored labs yet" body="Once this patient's lab report is verified and scored, the values appear here by domain." />;
  }
  return (
    <section className="rounded-[10px] border border-hairline bg-white px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-[20px] text-navy">Lab results</h3>
          <p className="mt-0.5 text-[13.5px] text-[#4d6072]">Collected {formatDate(data.labs.collectionDate)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/labs/${data.labs.labReportId}`} className="inline-flex h-[34px] items-center gap-2 rounded-md border border-[#c3d4cf] bg-white px-4 text-[14px] text-navy hover:bg-mint">
            <IconFlask size={16} />
            Open lab report
          </Link>
          <Link to={`/reports/${data.labs.reportId}`} className="inline-flex h-[34px] items-center rounded-md border border-[#c3d4cf] bg-white px-4 text-[14px] text-navy hover:bg-mint">
            View MCOS report
          </Link>
        </div>
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {data.labs.domains.map((domain) => (
          <div key={domain.key} className="rounded-lg border border-hairline">
            <div className="flex items-baseline justify-between gap-2 border-b border-hairline px-3.5 py-2.5">
              <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>{domain.label}</p>
              <p className="font-display text-[22px] text-navy">
                {domain.score ?? '-'}
                <span className="ml-0.5 text-[13px] text-[#6b7a88]">/100</span>
              </p>
            </div>
            <ul className="divide-y divide-hairline/60">
              {domain.markers.map((marker) => (
                <li key={marker.markerId} className="flex items-start justify-between gap-3 px-3.5 py-2">
                  <span className="min-w-0">
                    <span className="block text-[13.5px] text-navy">{marker.name}</span>
                    <span className={`block text-[12px] ${marker.flagged ? 'text-alert' : 'text-[#6b7a88]'}`}>{marker.bandLabel}</span>
                  </span>
                  <span className={`shrink-0 text-[13.5px] ${marker.flagged ? 'font-semibold text-alert' : 'text-navy'}`}>
                    {Number.isInteger(marker.value) ? marker.value : Number(marker.value.toFixed(2))} {marker.unit}
                  </span>
                </li>
              ))}
              {domain.markers.length === 0 && <li className="px-3.5 py-3 text-[12.5px] text-[#6b7a88]">No markers scored in this domain.</li>}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Tab 3: Clinical Twin insights
 * ------------------------------------------------------------------ */

function TwinTab({ data, selected }: { data: WorkspaceData; selected: string[] | null }) {
  const patientId = data.patient.id;
  const provider = useAuth().can('ADMIN', 'CLINICIAN');
  const treatmentIds = data.activeRegimen
    ? data.activeRegimen.treatments.map((row) => row.treatmentId)
    : (selected ?? []);
  const key = [...treatmentIds].sort().join(',');
  const preview = useQuery({
    queryKey: ['treatment-preview', patientId, key],
    queryFn: () => api.get<Preview>(`/regimens/preview${qs({ patientId, treatments: key })}`),
    enabled: provider && treatmentIds.length > 0,
  });
  const names = preview.data?.treatments.map((row) => row.name).join(' + ');

  if (treatmentIds.length === 0) {
    return <Empty title="Nothing to compare yet" body="Tick treatments on the MCOS Treatment Plan tab and the Clinical Twin comparison for that exact combination appears here." />;
  }
  return (
    <section className="rounded-[10px] border border-hairline bg-white px-4 py-4">
      <h3 className="font-display text-[20px] text-navy">Clinical Twin insights</h3>
      <p className="mt-0.5 text-[13.5px] text-[#4d6072]">
        {data.activeRegimen ? 'The combination this patient is on' : 'The combination ticked on the treatment plan tab'}
        {names ? `: ${names}` : ''}. Matched on phenotype, labs, body size, age and goal; only this exact combination counts.
      </p>
      <div className="mt-4 max-w-[560px]">
        <TwinPrediction cohort={preview.data?.cohort ?? null} loading={preview.isFetching && !preview.data} goal={data.patient.goalHeadline} />
      </div>
      {preview.data?.cohort.sufficient && preview.data.cohort.outcomes && (
        <dl className="mt-4 grid max-w-[560px] grid-cols-2 gap-3 text-[13.5px]">
          {preview.data.cohort.outcomes.continuationPct !== null && (
            <div className="rounded-lg bg-[#f2f6f4] px-3 py-2.5">
              <dt className="text-[#4d6072]">Still on treatment at {Math.round(preview.data.cohort.windowDays / 30)} months</dt>
              <dd className="font-display text-[20px] text-navy">{preview.data.cohort.outcomes.continuationPct}%</dd>
            </div>
          )}
          {preview.data.cohort.outcomes.glycemicImprovedPct !== null && (
            <div className="rounded-lg bg-[#f2f6f4] px-3 py-2.5">
              <dt className="text-[#4d6072]">A1c improved</dt>
              <dd className="font-display text-[20px] text-navy">{preview.data.cohort.outcomes.glycemicImprovedPct}%</dd>
            </div>
          )}
        </dl>
      )}
      <p className="mt-4 text-[12.5px] text-[#6b7a88]">
        Observed outcomes in similar patients, not a prediction for this patient. Below {preview.data?.cohort.minCohort ?? 30} matched
        patients MCOS shows no figure.
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Tab 4 and 5: history and notes
 * ------------------------------------------------------------------ */

function HistoryTab({ data }: { data: WorkspaceData }) {
  if (data.history.length === 0) return <Empty title="No history yet" body="Blood draws, reports, plans and check-ins appear here as they happen." />;
  return (
    <section className="rounded-[10px] border border-hairline bg-white px-4 py-4">
      <h3 className="font-display text-[20px] text-navy">Patient history</h3>
      <ol className="mt-3 space-y-0">
        {data.history.map((entry, index) => (
          <li key={`${entry.at}-${index}`} className="grid grid-cols-[110px_18px_minmax(0,1fr)] gap-3">
            <span className="pt-0.5 text-right text-[12.5px] text-[#6b7a88]">{formatDate(entry.at)}</span>
            <span className="relative flex justify-center">
              <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-forest" />
              {index < data.history.length - 1 && <span className="absolute top-4 bottom-0 w-px bg-hairline" />}
            </span>
            <span className="pb-3.5">
              <span className="block text-[14px] text-navy">{entry.title}</span>
              {entry.detail && <span className="block text-[12.5px] text-[#4d6072]">{entry.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

interface Note {
  id: string;
  body: string;
  createdAt: string;
  author: { id: string; name: string } | null;
}

function NotesTab({ patientId }: { patientId: string }) {
  const queryClient = useQueryClient();
  const notes = useQuery({
    queryKey: ['notes', patientId],
    queryFn: () => api.get<{ notes: Note[] }>(`/patients/${patientId}/notes`),
  });
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () => api.post<{ note: Note }>(`/patients/${patientId}/notes`, { body: text }),
    onSuccess: () => {
      setText('');
      void queryClient.invalidateQueries({ queryKey: ['notes', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['workspace', patientId] });
    },
  });

  return (
    <section className="rounded-[10px] border border-hairline bg-white px-4 py-4">
      <h3 className="font-display text-[20px] text-navy">Notes</h3>
      <form
        className="mt-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim()) add.mutate();
        }}
      >
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={4000}
          placeholder="Add a note for the care team"
          className="min-h-[96px] w-full rounded-lg border border-hairline bg-white px-3.5 py-2.5 text-[14px] text-navy placeholder:text-[#8a97a3] focus:outline-none focus:border-forest/50"
        />
        {add.error && <p className="mt-1 text-[12.5px] text-alert">{add.error instanceof Error ? add.error.message : 'Could not save'}</p>}
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={!text.trim() || add.isPending}
            className="inline-flex h-[36px] items-center gap-2 rounded-md bg-forest px-4 text-[14px] font-medium text-white hover:bg-forest-deep disabled:opacity-50"
          >
            <IconNote size={16} />
            Add Note
          </button>
        </div>
      </form>
      {notes.isLoading ? (
        <LoadingRows rows={2} />
      ) : (notes.data?.notes.length ?? 0) === 0 ? (
        <p className="mt-2 text-[13px] text-[#6b7a88]">No notes yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-hairline/70">
          {notes.data!.notes.map((note) => (
            <li key={note.id} className="py-3">
              <p className="whitespace-pre-wrap text-[14px] text-navy">{note.body}</p>
              <p className="mt-1 text-[12px] text-[#6b7a88]">
                {note.author?.name ?? 'Former user'} · {formatDate(note.createdAt)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-[10px] border border-dashed border-hairline bg-white px-5 py-10 text-center">
      <p className="font-display text-[18px] text-navy">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-[13.5px] text-[#4d6072]">{body}</p>
    </section>
  );
}

